import { observable, action, computed } from 'mobx';
import { flatten } from 'lodash';
import { v4 as uuidv4 } from 'uuid';
import { ApplicationDefinitionConditionalEditStore } from '../stores';
import { InputGroupDto, BindingGroupDto, LayoutNodeDto } from '../types';
import { BindingGroupModel, InputModel } from '.';
import { InputBinding, InputMeta } from '../../iota_applications/types';
import { ApplicationDefinitionConditionalHelper } from '../misc';
import { getDefaultBindingGroups } from '../misc/defaultBindingGroups';
import { defaultChildBindingGroup } from '../misc/defaultChildBindingGroup';
import { serializeBindingGroupContent } from '../misc/serializeBindingGroupContent';

const mainClassInputId = 'MainClass';

export default class InputGroupModel {
    @observable
    name: string;

    @observable
    enabled: boolean;

    @observable
    isDefault: boolean;

    @observable
    editable: boolean = false;

    @observable
    bindingGroups: BindingGroupModel[] = [];

    @observable
    bindingGroupsVersion: string = uuidv4();

    @observable
    layoutSettingsId: string | null = null;

    @observable
    openBindingGroupIds: string[] = [];

    @computed
    get editableBindingGroup() {
        return this.bindingGroups.find(g => g.editable);
    }

    @computed
    get tabKey() {
        return `${this.inputGroupId}_${this.isDefault}`;
    }

    @computed
    get layoutSettings() {
        return this.store.applicationDefinitionLayoutStructure?.layoutSettings.find(
            s => s.id === this.layoutSettingsId
        );
    }

    get defaultBindingGroups() {
        return getDefaultBindingGroups(this.store, this.layoutSettingsId);
    }

    get dto(): InputGroupDto {
        return {
            inputGroupId: this.inputGroupId,
            name: this.name,
            enabled: this.enabled,
            isDefault: this.isDefault,
            layoutSettingsId: this.layoutSettingsId,
            bindingGroups: serializeBindingGroupContent(this.bindingGroups, this.defaultBindingGroups)
        };
    }

    readonly inputGroupId: string;

    constructor(
        private readonly store: ApplicationDefinitionConditionalEditStore,
        data: InputGroupDto
    ) {
        this.inputGroupId = data.inputGroupId;
        this.name = data.name;
        this.enabled = data.enabled;
        this.isDefault = data.isDefault;
        this.layoutSettingsId = data.layoutSettingsId;

        const hiddenSections = this.layoutSettings?.hiddenSections ?? [];
        const filteredSections = this.store.applicationDefinitionSectionNames.filter(s => !hiddenSections.includes(s));

        this.bindingGroups = filteredSections.map(section => this.createBindingGroup(section, data.bindingGroups));
    }

    @action.bound
    update(data: Partial<InputGroupDto>) {
        this.name = data.name ? data.name.trim() : this.name;
        this.enabled = data.enabled ?? this.enabled;
        this.isDefault = data.isDefault ?? this.isDefault;
        this.layoutSettingsId = data.layoutSettingsId ?? this.layoutSettingsId;
        this.store.setIsDataChanged(true);
    }

    @action.bound
    setEditable(editable: boolean) {
        this.editable = editable;
    }

    @action.bound
    setOpenBindingGroupIds(openBindingGroupIds: string[]) {
        this.openBindingGroupIds = openBindingGroupIds;
    }

    @action.bound
    setBindingGroups(bindingGroups: BindingGroupModel[]) {
        this.bindingGroups = bindingGroups;
        this.bindingGroupsVersion = uuidv4();
    }

    getInputById(inputId: string) {
        const searchInBindingGroups = (bindingGroups: BindingGroupModel[]): InputModel | undefined => {
            for (const bindingGroup of bindingGroups) {
                const input = bindingGroup.getInputById(inputId);

                if (input) {
                    return input;
                }

                const childInput = searchInBindingGroups(bindingGroup.childBindingGroups);

                if (childInput) {
                    return childInput;
                }
            }

            return undefined;
        };

        return searchInBindingGroups(this.bindingGroups);
    }

    delete() {
        if (!this.store.canDeleteInputGroups) {
            return;
        }
        this.store.removeInputGroup(this);
        this.store.setIsDataChanged(true);
    }

    private createBindingGroup(section: string, bindingGroups: BindingGroupDto[]): BindingGroupModel {
        const bindingGroupId = section.replace(/\s+/g, '');
        const existingBindingGroup = bindingGroups.find(g => g.bindingGroupId === bindingGroupId);

        const bindingGroup = new BindingGroupModel(
            bindingGroupId,
            existingBindingGroup?.name ?? section,
            existingBindingGroup?.enabled ?? true
        );

        if (
            !this.layoutSettings?.layoutNodes.length ||
            this.layoutSettings.sectionsWithDefaultLayout.includes(section)
        ) {
            bindingGroup.setInputs(this.createInputs(section, existingBindingGroup?.bindings));
            return bindingGroup;
        }

        const childBindingGroups = this.layoutSettings.layoutNodes.map(layoutNode =>
            this.createChildBindingGroups(section, layoutNode, existingBindingGroup?.childBindingGroups ?? [])
        );

        bindingGroup.setChildBindingGroups(flatten(childBindingGroups));

        return bindingGroup;
    }

    private createChildBindingGroups(
        section: string,
        layoutNode: LayoutNodeDto,
        existingChildBindingGroups: BindingGroupDto[],
        parentBindingGroupId?: string
    ): BindingGroupModel[] {
        return Array.from({ length: layoutNode.count }, (_, i) => {
            const { bindingGroupId, name: bindingGroupName } = defaultChildBindingGroup(layoutNode, i + 1);

            const existingChildBindingGroup = existingChildBindingGroups.find(g => g.bindingGroupId === bindingGroupId);

            const bindingGroup = new BindingGroupModel(
                bindingGroupId,
                existingChildBindingGroup?.name ?? bindingGroupName,
                existingChildBindingGroup?.enabled ?? true
            );

            if (layoutNode.childNodes.length) {
                const childChildBindingGroups = layoutNode.childNodes.map(childLayoutNode =>
                    this.createChildBindingGroups(
                        section,
                        childLayoutNode,
                        existingChildBindingGroup?.childBindingGroups ?? [],
                        bindingGroupId
                    )
                );

                bindingGroup.setChildBindingGroups(flatten(childChildBindingGroups));
            } else {
                bindingGroup.setInputs(
                    this.createInputs(
                        section,
                        existingChildBindingGroup?.bindings,
                        parentBindingGroupId
                            ? `${parentBindingGroupId}${ApplicationDefinitionConditionalHelper.ParentChildBindingGroupDelimiter}${bindingGroup.bindingGroupId}`
                            : bindingGroup.bindingGroupId
                    )
                );
            }

            return bindingGroup;
        });
    }

    private createInputs(section: string, existingBindings?: InputBinding[], idPrefix?: string): InputModel[] {
        const inputsToHide =
            this.layoutSettings?.sectionsHiddenInputs.find(s => s.section === section)?.inputsToHide ?? [];
        const hiddenInputIds = new Set(inputsToHide);
        const hiddenClassifications = new Set<InputMeta>();
        const sectionInputs = this.store.applicationDefinitionInputs.filter(input => input.section === section);

        return sectionInputs
            .filter(
                input =>
                    !hiddenInputIds.has(input.id) &&
                    !this.hasHiddenClassificationAnchorOrParent(
                        input,
                        sectionInputs,
                        hiddenInputIds,
                        hiddenClassifications
                    )
            )
            .map(inputMeta => {
                const inputId = idPrefix
                    ? `${idPrefix}${ApplicationDefinitionConditionalHelper.InputIdDelimiter}${inputMeta.id}`
                    : inputMeta.id;

                const bindings = existingBindings?.filter(b => b.inputId === inputId) ?? [];

                return new InputModel(inputId, inputMeta, bindings);
            });
    }

    private hasHiddenClassificationAnchorOrParent(
        input: InputMeta,
        sectionInputs: InputMeta[],
        hiddenInputIds: Set<string>,
        hiddenClassifications: Set<InputMeta>,
        visitedInputIds: Set<string> = new Set()
    ): boolean {
        if (!input.isAdditionalClassification) {
            return false;
        }

        if (hiddenClassifications.has(input)) {
            return true;
        }

        if (hiddenInputIds.has(mainClassInputId)) {
            hiddenClassifications.add(input);
            return true;
        }

        const parentInputIds = this.getParentInputIds(input);
        if (parentInputIds.length === 0) {
            return false;
        }

        const hidden = parentInputIds.every(parentInputId =>
            this.hasHiddenClassificationParent(
                parentInputId,
                sectionInputs,
                hiddenInputIds,
                hiddenClassifications,
                visitedInputIds
            )
        );
        if (hidden) {
            hiddenClassifications.add(input);
        }

        return hidden;
    }

    private hasHiddenClassificationParent(
        parentInputId: string,
        sectionInputs: InputMeta[],
        hiddenInputIds: Set<string>,
        hiddenClassifications: Set<InputMeta>,
        visitedInputIds: Set<string>
    ): boolean {
        if (hiddenInputIds.has(parentInputId)) {
            return true;
        }

        if (visitedInputIds.has(parentInputId)) {
            return false;
        }

        const parentInput = sectionInputs.find(candidate => candidate.id === parentInputId);
        if (!parentInput) {
            return false;
        }

        return this.hasHiddenClassificationAnchorOrParent(
            parentInput,
            sectionInputs,
            hiddenInputIds,
            hiddenClassifications,
            new Set([...visitedInputIds, parentInputId])
        );
    }

    private getParentInputIds(input: InputMeta): string[] {
        return input.parentInputIds?.length ? input.parentInputIds : input.parentInputId ? [input.parentInputId] : [];
    }
}
